/**
 * Offer ID Helper Utilities
 *
 * This module provides utilities for extracting and validating offer IDs from URLs.
 *
 * Context: Staff members often paste offer URLs into messages with trailing punctuation
 * (e.g., "Check out https://staff.ourbranch.com/offer/{uuid}."), which causes the
 * punctuation to be included in the URL parameter, resulting in invalid offer IDs.
 *
 * Related: GitHub Issue #27512
 */

/**
 * Extracts and validates a clean UUID from a potentially malformed offerId string.
 *
 * Handles cases where offerId includes trailing punctuation, text, etc.
 * Returns the cleaned UUID if valid, otherwise returns the original string.
 *
 * Examples:
 *   '9da6c39e-5fc1-497c-854f-b0f54e7ce084' -> '9da6c39e-5fc1-497c-854f-b0f54e7ce084'
 *   '9da6c39e-5fc1-497c-854f-b0f54e7ce084.' -> '9da6c39e-5fc1-497c-854f-b0f54e7ce084'
 *   '9da6c39e-5fc1-497c-854f-b0f54e7ce084!@#' -> '9da6c39e-5fc1-497c-854f-b0f54e7ce084'
 *   'invalid-id' -> 'invalid-id' (returns original, validation happens at API layer)
 *
 * @param {string} offerId - The offerId from URL params, potentially with trailing characters
 * @returns {string} The cleaned UUID, or original if no valid UUID pattern found
 */
export const extractCleanOfferId = (offerId) => {
  if (!offerId || typeof offerId !== 'string') {
    return offerId;
  }

  // UUID v4 regex pattern: 8-4-4-4-12 hexadecimal digits
  // Matches at the start of the string and captures only the UUID portion
  const uuidPattern = /^([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i;
  const match = offerId.match(uuidPattern);

  return match ? match[1] : offerId;
};

/**
 * Validates if a string is a valid UUID v4 format.
 *
 * @param {string} offerId - The offerId to validate
 * @returns {boolean} True if valid UUID v4 format
 */
export const isValidUUID = (offerId) => {
  if (!offerId || typeof offerId !== 'string') {
    return false;
  }

  // UUID v4 regex pattern - must match the entire string
  const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidPattern.test(offerId);
};
